import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface DropdownProps {
    label?: React.ReactNode;
    trigger?: React.ReactNode;
    children: React.ReactNode;
    align?: 'left' | 'right';
}

const Dropdown: React.FC<DropdownProps> = ({
    label,
    trigger,
    children,
    align = 'right'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);

    const updateCoords = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCoords({
                top: rect.bottom + window.scrollY,
                left: align === 'right'
                    ? rect.right + window.scrollX
                    : rect.left + window.scrollX
            });
        }
    };

    const toggleDropdown = (e: React.MouseEvent) => {
        e.stopPropagation();
        updateCoords();
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
            window.addEventListener('resize', () => setIsOpen(false));
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            window.removeEventListener('resize', () => setIsOpen(false));
        };
    }, [isOpen]);

    return (
        <div className="relative inline-block" ref={triggerRef}>
            <div onClick={toggleDropdown} className="inline-block">
                {trigger ? trigger : (
                    <button className="flex items-center justify-between gap-2 px-4 py-2  rounded-md text-slate-700 hover:bg-slate-50 transition-all cursor-pointer">
                        {label || ''}
                    </button>
                )}
            </div>

            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -5 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -5 }}
                            style={{
                                position: 'absolute',
                                top: coords.top + 4,
                                left: align === 'right' ? coords.left - 144 : coords.left,
                                zIndex: 99999,
                                pointerEvents: 'auto'
                            }}
                            className="w-36 rounded-md bg-white border border-slate-200 shadow-xl p-1 overflow-hidden"
                        >
                            <div
                                className="flex flex-col gap-1"
                                onClick={() => setIsOpen(false)} // إغلاق عند الضغط على أي خيار
                            >
                                {children}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

const DropdownItem: React.FC<{
    onClick?: () => void;
    children: React.ReactNode;
    className?: string;
}> = ({ onClick, children, className }) => {
    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                onClick?.();
            }}
            className={`w-full text-right px-3 py-2 text-sm text-slate-700 hover:bg-violet-50 hover:text-violet-700 rounded-sm transition-colors cursor-pointer flex items-center gap-2 ${className}`}
        >
            {children}
        </button>
    );
};

export { Dropdown, DropdownItem };